<template>
  <v-dialog v-model="show_dialog" width="480">
    <template v-slot:activator="{ on }">
      <v-icon
        class="sync-status-indicator"
        v-bind:class="{ 'sync-indicator-color-animation': animate_color }"
        small
        :color="status_indicator_color"
        v-on="on"
        >brightness_1</v-icon
      >
    </template>
    <v-card>
      <v-card-title>Synchronisierungsstatusmeldungen</v-card-title>
      <v-card-text>
        <div v-if="messages.length > 0">
          <v-row no-gutters v-for="(message, i) in messages" :key="i">
            <v-col cols="12">
              <v-card flat class="mt-1">
                <div :style="`background-color:${message.success ? '#C8E6C9' : '#FFCDD2'}`">
                  <div class="mx-1">
                    {{ message.message_text }}<br />
                    <i>{{ message.tstamp.fromNow() }}</i>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>
        <p v-else>Keine aktuellen Meldungen zum Synchronisierungsstatus.</p>
      </v-card-text>
      <v-card-actions>
        <v-btn text @click="show_dialog = false">Schließen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";

const sync_status = {
  NEUTRAL: "NEUTRAL",
  TOUCHED: "TOUCHED",
  SYNCING: "SYNCING",
  SUCCESS: "SUCCESS",
  FAILURE: "FAILURE",
};
const STATUS_COLOR = {
  NEUTRAL: "grey lighten-2",
  TOUCHED: "warning lighten-2",
  SYNCING: "info lighten-2",
  SUCCESS: "success lighten-1",
  FAILURE: "error lighten-2",
};
const SYNC_TYPE_DICT = {
  noun: {
    create: "Erstellen",
    update: "Änderung",
    delete: "Löschen",
    save: "Speichern",
  },
  verb: {
    create: "erstellt",
    update: "geändert",
    delete: "gelöscht",
    save: "gespeichert",
  },
};
const SYNC_MESSAGES_CACHE_SIZE = 10;
const SYNC_MESSAGES_STATUS_LOCK_SECONDS = 5;

class SyncMessage {
  constructor(message) {
    this.entity_type = message.entity_type;
    this.entity_label = message.entity_label;
    this.sync_type = message.sync_type;
    this.error_message = message.error;
    this.success = message.success;
    this.tstamp = moment();
    this.message_text = this.construct_message_text();
  }
  construct_message_text() {
    var text;
    if (this.success) {
      text = `${this.entity_type} ${this.entity_label} erfolgreich ${SYNC_TYPE_DICT.verb[this.sync_type]}.`;
    } else {
      text = `Fehler: ${SYNC_TYPE_DICT.noun[this.sync_type]} von ${this.entity_type} ${
        this.entity_label
      } fehlgeschlagen. Fehlermeldung: ${this.error_message}.`;
    }
    return text;
  }
}

export default {
  name: "SyncStatusIndicator",
  mounted() {
    this.init_listeners();
  },
  watch: {
    status(new_status, old_status) {
      this.animate_color = new_status === sync_status.SYNCING;
      window.clearTimeout(this.reset_timer);
      this.reset_timer = null;
      if (new_status === sync_status.SUCCESS || new_status === sync_status.FAILURE) {
        this.reset_timer = window.setTimeout(() => {
          this.status = sync_status.NEUTRAL;
          this.status_indicator_color = STATUS_COLOR.NEUTRAL;
        }, SYNC_MESSAGES_STATUS_LOCK_SECONDS * 1000);
      }
    },
  },
  methods: {
    init_listeners() {
      this.$eventHub.on("sync-status-neutral", this.set_status_neutral);
      this.$eventHub.on("sync-status-touched", this.set_status_touched);
      this.$eventHub.on("sync-status-syncing", this.set_status_syncing);
      this.$eventHub.on("sync-status-success", this.set_status_success);
      this.$eventHub.on("sync-status-failure", this.set_status_failure);
    },
    set_status_neutral() {
      if (!this.reset_timer) {
        this.status = sync_status.NEUTRAL;
        this.status_indicator_color = STATUS_COLOR.NEUTRAL;
      }
    },
    set_status_touched() {
      this.status = sync_status.TOUCHED;
      this.status_indicator_color = STATUS_COLOR.TOUCHED;
    },
    set_status_syncing() {
      if (this.status !== sync_status.SYNCING) {
        this.status = sync_status.SYNCING;
        this.status_indicator_color = STATUS_COLOR.SYNCING;
      }
    },
    set_status_success(message) {
      this.status = sync_status.SUCCESS;
      this.status_indicator_color = STATUS_COLOR.SUCCESS;
      message.success = true;
      this.add_message(message);
    },
    set_status_failure(message) {
      this.status = sync_status.FAILURE;
      this.status_indicator_color = STATUS_COLOR.FAILURE;
      message.success = false;
      this.add_message(message);
    },
    add_message(message) {
      this.messages.unshift(new SyncMessage(message));
      if (this.messages.length > SYNC_MESSAGES_CACHE_SIZE) {
        this.messages.splice(SYNC_MESSAGES_CACHE_SIZE);
      }
    },
  },
  data: function() {
    return {
      status: sync_status.NEUTRAL,
      status_indicator_color: STATUS_COLOR.NEUTRAL,
      reset_timer: null,
      cycle_syncing_counter: 0,
      animate_color: false,
      show_dialog: false,
      messages: [],
    };
  },
};
</script>

<style lang="scss" scoped>
@-webkit-keyframes color_change {
  from {
    color: #64b5f6;
  }
  to {
    color: #e3f2fd;
  }
}
@-moz-keyframes color_change {
  from {
    color: #64b5f6;
  }
  to {
    color: #e3f2fd;
  }
}
@-ms-keyframes color_change {
  from {
    color: #64b5f6;
  }
  to {
    color: #e3f2fd;
  }
}
@-o-keyframes color_change {
  from {
    color: #64b5f6;
  }
  to {
    color: #e3f2fd;
  }
}
@keyframes color_change {
  from {
    color: #64b5f6;
  }
  to {
    color: #e3f2fd;
  }
}

.sync-indicator-color-animation {
  -webkit-animation: color_change 1s infinite alternate;
  -moz-animation: color_change 1s infinite alternate;
  -ms-animation: color_change 1s infinite alternate;
  -o-animation: color_change 1s infinite alternate;
  animation: color_change 1s infinite alternate;
}
.sync-status-indicator {
  transform: translate(76pt, -7pt);
}
</style>
